import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

export const inputClasses =
  'h-11 w-full rounded-xl bg-ink/[0.05] px-3.5 text-[15px] text-ink transition-[background-color,box-shadow] duration-200 ' +
  'placeholder:text-ink-faint focus:bg-surface focus:shadow-[0_0_0_3px_rgb(var(--accent)/0.25),0_0_0_1px_rgb(var(--accent)/0.6)] focus:outline-none ' +
  'disabled:opacity-60 dark:bg-white/[0.06] aria-[invalid=true]:shadow-[0_0_0_1px_rgb(var(--danger)/0.7)]'

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string
  hint?: ReactNode
  trailing?: ReactNode
}

/** Labelled text input. */
export const TextField = forwardRef<HTMLInputElement, FieldProps>(function TextField(
  { label, hint, trailing, className, id, ...props },
  ref,
) {
  const autoId = useId()
  const inputId = id ?? autoId
  const hintId = hint ? `${inputId}-hint` : undefined
  return (
    <div className={className}>
      <label htmlFor={inputId} className="mb-1.5 block text-[13px] font-medium text-ink-muted">
        {label}
      </label>
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          aria-describedby={hintId}
          className={cn(inputClasses, trailing ? 'pr-11' : undefined)}
          {...props}
        />
        {trailing && <div className="absolute inset-y-0 right-1.5 flex items-center">{trailing}</div>}
      </div>
      {hint && (
        <p id={hintId} className="mt-1.5 text-xs leading-5 text-ink-faint">
          {hint}
        </p>
      )}
    </div>
  )
})

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string
  hint?: ReactNode
  children: ReactNode
}

export function SelectField({ label, hint, className, id, children, ...props }: SelectFieldProps) {
  const autoId = useId()
  const selectId = id ?? autoId
  const hintId = hint ? `${selectId}-hint` : undefined
  return (
    <div className={className}>
      <label htmlFor={selectId} className="mb-1.5 block text-[13px] font-medium text-ink-muted">
        {label}
      </label>
      <select
        id={selectId}
        aria-describedby={hintId}
        className={cn(inputClasses, 'appearance-auto pr-2')}
        {...props}
      >
        {children}
      </select>
      {hint && (
        <p id={hintId} className="mt-1.5 text-xs leading-5 text-ink-faint">
          {hint}
        </p>
      )}
    </div>
  )
}
